import { Lightning } from "@lightningjs/sdk";
import { List } from "@lightningjs/ui";
import { ChannelModel, ChannelItemModel, ProgramModel } from "../../../../lib/models";
import { ChannelItem } from "../ChannelItem/ChannelItem";
import { GroupTitle } from "../GroupTitle/GroupTitle";
import { getLiveTvChannels } from "../../../../api/queries";

export interface LiveTvGroup {
    name: string;
    channels: ChannelModel[];
}

export abstract class ChannelListBase extends Lightning.Component<any> {
    protected _channels: ChannelModel[] = [];
    protected _groups: LiveTvGroup[] = [];

    protected abstract get _list(): List;

    override _getFocused(): Lightning.Component | null | undefined {
        return this._list;
    }

    _calculateProgress(program: ProgramModel | undefined, now: Date): number {
        if (!program) return 0;
        const start = new Date(program.start);
        const stop = new Date(program.stop);
        const totalDuration = stop.getTime() - start.getTime();
        const elapsed = now.getTime() - start.getTime();
        return Math.round((elapsed / totalDuration) * 100);
    }

    _findNextProgram(programs: ProgramModel[], now: Date): ProgramModel | undefined {
        return programs.find((program) => now < new Date(program.start));
    }

    _findCurrentProgram(programs: ProgramModel[], now: Date): ProgramModel | undefined {
        return programs.find((program) => {
            const start = new Date(program.start);
            const stop = new Date(program.stop);
            return now >= start && now < stop;
        });
    }

    _createChannelItem = (channel: ChannelModel, index: number) => {
        const now = new Date();
        const currentProgram = channel.programs ? this._findCurrentProgram(channel.programs, now) : undefined;
        const nextProgram = channel.programs ? this._findNextProgram(channel.programs, now) : undefined;
        const progress = this._calculateProgress(currentProgram, now);

        return {
            type: ChannelItem,
            w: 610,
            item: {
                ...channel,
                channelNumber: (index + 1).toString().padStart(3, "0"),
                currentProgram,
                nextProgram,
                progress
            }
        };
    };

    reload(channels: ChannelModel[]): void {
        this._channels = channels;
        this._list.reload(channels.map(this._createChannelItem));
    }

    addItems(channels: ChannelModel[]): void {
        const startIndex = this._channels.length;
        this._channels = [...this._channels, ...channels];

        if (this._list.items?.length) {
            this._list.add(channels.map((ch, i) => this._createChannelItem(ch, startIndex + i)));
        } else {
            this.reload(channels);
        }
    }

    addGroups(groups: LiveTvGroup[]): void {
        this._groups = groups;
        this._channels = groups.flatMap((g) => g.channels);
        this._list.reload(this._createGroupedItems(groups));
        this._list.enableRequests = false;
        this._setInitialFocus();
    }

    _setInitialFocus(): void {
        const firstChannelIndex = this._list.items.findIndex((item: any) => !item.isGroupTitle);
        if (firstChannelIndex !== -1) {
            this._list.setIndex(firstChannelIndex);
        }
    }

    _createGroupedItems(groups: LiveTvGroup[]): any[] {
        const items: any[] = [];
        let channelIndex = 0;

        groups.forEach((group) => {
            items.push({ type: GroupTitle, title: group.name, isGroupTitle: true });
            group.channels.forEach((channel) => {
                items.push(this._createChannelItem(channel, channelIndex++));
            });
        });

        return items;
    }

    async onRequestItems() {
        return (await getLiveTvChannels(this._list.items.length)).map((channel: ChannelModel, index: number) =>
            this._createChannelItem(channel, this._list.items.length + index)
        );
    }

    navigateNext(): ChannelItemModel | null {
        const currentIndex = this._list.index;
        let newIndex = currentIndex + 1;

        while (newIndex < this._list.items.length && this._list.items[newIndex]?.isGroupTitle) {
            newIndex++;
        }

        if (newIndex >= this._list.items.length) {
            for (let i = 0; i < this._list.items.length; i++) {
                if (!this._list.items[i]?.isGroupTitle) {
                    this._list.setIndex(i);
                    return this._list.items[i]?.item ?? null;
                }
            }
            return null;
        }

        this._list.setIndex(newIndex);
        return this._list.items[newIndex]?.item ?? null;
    }

    navigateLast(): ChannelItemModel | null {
        for (let i = this._list.items.length - 1; i >= 0; i--) {
            if (!this._list.items[i]?.isGroupTitle) {
                this._list.setIndex(i);
                return this._list.items[i]?.item ?? null;
            }
        }
        return null;
    }

    navigatePrev(): ChannelItemModel | null {
        const currentIndex = this._list.index;
        let newIndex = currentIndex - 1;

        while (newIndex >= 0 && this._list.items[newIndex]?.isGroupTitle) {
            newIndex--;
        }

        if (newIndex < 0) return null;
        this._list.setIndex(newIndex);
        return this._list.items[newIndex]?.item ?? null;
    }

    get items() {
        return this._list.items;
    }

    get currentItem() {
        return this._list.currentItem;
    }

    get index() {
        return this._list.index;
    }

    set index(value: number) {
        const targetIndex = this._findNearestChannelIndex(value);
        if (targetIndex !== -1) {
            this._list.setIndex(targetIndex);
        }
    }

    _findNearestChannelIndex(targetIndex: number): number {
        if (targetIndex >= 0 && targetIndex < this._list.items.length) {
            const item = this._list.items[targetIndex];
            if (item && !item.isGroupTitle) return targetIndex;
        }

        const nextIndex = this._findNextChannelIndex(targetIndex);
        if (nextIndex !== -1) return nextIndex;

        const prevIndex = this._findPreviousChannelIndex(targetIndex);
        return prevIndex;
    }

    _findNextChannelIndex(startIndex: number): number {
        for (let i = startIndex + 1; i < this._list.items.length; i++) {
            if (!this._list.items[i].isGroupTitle) return i;
        }
        return -1;
    }

    _findPreviousChannelIndex(startIndex: number): number {
        for (let i = startIndex - 1; i >= 0; i--) {
            if (!this._list.items[i].isGroupTitle) return i;
        }
        return -1;
    }
}